import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { websiteDb, signInWebsiteReader, isConfigured as isWebsiteConfigured } from '../../lib/websiteFirebase';

// ---------------------------------------------------------------------------
// Types — mirror the website Firestore schema
// ---------------------------------------------------------------------------

export interface Submission {
  firstName:  string;
  lastName:   string;
  email:      string;
  phone:      string;
  subject:    string;
  message:    string;
  source:     string;
  createdAt:  Timestamp;
  emailSent?: boolean;
}

// The five onboarding sections of a Subscription Website order. All values are
// strings and any may be empty. NOTE: `environment` deliberately holds provider
// NAMES only — never passwords/credentials (those are handled off-platform). Do
// not add any credential-entry UI around this data.
export interface SubscriptionFormData {
  business?:    { businessName?: string; abn?: string; contactName?: string; phone?: string; email?: string };
  profile?:     { industry?: string; description?: string; serviceArea?: string; openingHours?: string; socials?: string };
  content?:     { hasExistingSite?: string; existingUrl?: string; pages?: string; tagline?: string; keyServices?: string; contentReady?: string; assetsLink?: string };
  environment?: { hasDomain?: string; domainName?: string; registrar?: string; hostingProvider?: string; canDelegate?: string; transferDomain?: string };
  goals?:       { goals?: string; competitors?: string; notes?: string };
}

// The `orders` collection holds several order types. Audit fields (firstName/…/
// notes) exist on 'shopify_audit'; the free 'automation_audit' additionally
// carries a rich `formData` map (business/systems/goals). Subscription fields
// (customerName/formData/…) exist on 'website_subscription'. Statuses differ per
// type (audits can be 'submitted'). We render audit `formData` GENERICALLY, so
// any field the website adds shows automatically without an app change.
export interface AuditFormData {
  business?: { name?: string; website?: string; industry?: string; employees?: string; yearsInBusiness?: string; revenueRange?: string };
  systems?:  { currentTools?: string; integrationLevel?: string; weeklyAdminHours?: string; manualWorkArea?: string };
  goals?:    { topPainPoints?: string; desiredOutcomes?: string; notes?: string };
  [section: string]: Record<string, any> | undefined; // forward-compatible: unknown sections still render
}

export interface Order {
  type:            'shopify_audit' | 'automation_audit' | 'website_subscription';
  formName?:       string;
  // shared
  email:           string;
  phone?:          string;
  businessName?:   string;
  storeName?:      string;   // audit: store name · subscription: business name
  status:          'pending' | 'paid' | 'submitted';
  createdAt:       Timestamp;
  stripeSessionId?: string;
  paidAt?:         Timestamp;
  // audit
  firstName?:      string;
  lastName?:       string;
  storeUrl?:       string;
  notes?:          string;
  // website_subscription
  customerName?:   string;
  amount?:         number;   // AUD/month (normally 99)
  testMode?:       boolean;  // $1 test-mode run — not real revenue
  formData?:       SubscriptionFormData | AuditFormData;
  stripeSubscriptionId?: string;
}

export interface InvoicePayment {
  invoiceNumber:   string;
  email:           string;
  amount:          number;
  status:          'pending' | 'paid';
  createdAt:       Timestamp;
  stripeSessionId?: string;
  paidAt?:         Timestamp;
}

export type LeadType =
  | 'enquiry'
  | 'audit_pending'
  | 'audit_submitted'
  | 'audit_paid'
  | 'invoice_pending'
  | 'invoice_paid'
  | 'subscription_submitted'
  | 'subscription_paid';

export interface LeadEntry {
  id:            string;
  type:          LeadType;
  collection:    'submissions' | 'orders' | 'invoice_payments';
  firstName:     string;
  lastName:      string;
  email:         string;
  phone?:        string;
  subject?:      string;
  message?:      string;
  storeName?:    string;
  storeUrl?:     string;
  invoiceNumber?: string;
  amount?:       number;
  source?:       string;
  createdAt:     Date;
  paidAt?:       Date;
  // website_subscription extras
  testMode?:     boolean;
  formData?:     SubscriptionFormData;
  // audit extras (shopify_audit / automation_audit)
  formName?:     string;                          // e.g. "Automation Audit"
  auditData?:    Record<string, Record<string, any>>; // raw formData, rendered generically
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface WebsiteLeadsContextType {
  leads:           LeadEntry[];
  loading:         boolean;
  newLeadsCount:   number;
  isPanelOpen:     boolean;
  openLeadsPanel:  () => void;
  closeLeadsPanel: () => void;
  deleteLead:      (lead: LeadEntry) => Promise<void>;
}

const WebsiteLeadsContext = createContext<WebsiteLeadsContextType>({
  leads:           [],
  loading:         true,
  newLeadsCount:   0,
  isPanelOpen:     false,
  openLeadsPanel:  () => {},
  closeLeadsPanel: () => {},
  deleteLead:      async () => {},
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toDate(ts: Timestamp | undefined): Date {
  return ts instanceof Timestamp ? ts.toDate() : new Date(0);
}

function submissionToLead(id: string, d: Submission): LeadEntry {
  return {
    id,
    type:       'enquiry',
    collection: 'submissions',
    firstName:  d.firstName,
    lastName:   d.lastName,
    email:      d.email,
    phone:      d.phone,
    subject:    d.subject,
    message:    d.message,
    source:     d.source,
    createdAt:  toDate(d.createdAt),
  };
}

function orderToLead(id: string, d: Order): LeadEntry {
  if (d.type === 'website_subscription') {
    const sfd = d.formData as SubscriptionFormData | undefined;
    return {
      id,
      type:       d.status === 'paid' ? 'subscription_paid' : 'subscription_submitted',
      collection: 'orders',
      firstName:  d.customerName || sfd?.business?.contactName || '',
      lastName:   '',
      email:      d.email || sfd?.business?.email || '',
      phone:      d.phone || sfd?.business?.phone,
      storeName:  d.storeName || sfd?.business?.businessName,
      amount:     d.amount,
      testMode:   d.testMode,
      formData:   sfd,
      createdAt:  toDate(d.createdAt),
      paidAt:     d.paidAt ? toDate(d.paidAt) : undefined,
    };
  }
  // shopify_audit / automation_audit — carry the full formData through so every
  // collected field can be shown (audits may be 'submitted' rather than paid).
  const auditType: LeadType =
    d.status === 'paid' ? 'audit_paid' : d.status === 'submitted' ? 'audit_submitted' : 'audit_pending';
  return {
    id,
    type:       auditType,
    collection: 'orders',
    firstName:  d.firstName || '',
    lastName:   d.lastName || '',
    email:      d.email,
    phone:      d.phone,
    storeName:  d.storeName || d.businessName,
    storeUrl:   d.storeUrl,
    message:    d.notes,
    formName:   d.formName,
    auditData:  (d.formData as Record<string, Record<string, any>> | undefined) || undefined,
    createdAt:  toDate(d.createdAt),
  };
}

function paymentToLead(id: string, d: InvoicePayment): LeadEntry {
  return {
    id,
    type:          d.status === 'paid' ? 'invoice_paid' : 'invoice_pending',
    collection:    'invoice_payments',
    firstName:     '',
    lastName:      '',
    email:         d.email,
    invoiceNumber: d.invoiceNumber,
    amount:        d.amount,
    createdAt:     toDate(d.createdAt),
  };
}

function toastForLead(lead: LeadEntry) {
  const name = [lead.firstName, lead.lastName].filter(Boolean).join(' ') || lead.email;

  switch (lead.type) {
    case 'enquiry':
      toast.info(`💬 New enquiry from ${name}`, { autoClose: 6000 });
      break;
    case 'audit_pending':
      toast.warning(`🛒 New Shopify audit order from ${name}`, { autoClose: 6000 });
      break;
    case 'audit_submitted':
      toast.warning(`📋 New ${lead.formName || 'audit'} from ${name}`, { autoClose: 7000 });
      break;
    case 'audit_paid':
      toast.success(`💰 Shopify audit paid by ${name}`, { autoClose: 6000 });
      break;
    case 'invoice_pending':
      toast.info(`🧾 Invoice payment initiated — ${lead.invoiceNumber}`, { autoClose: 6000 });
      break;
    case 'invoice_paid':
      toast.success(`✅ Invoice paid — ${lead.invoiceNumber} ($${lead.amount?.toLocaleString()})`, { autoClose: 6000 });
      break;
    case 'subscription_submitted':
      toast.warning(`🔥 New Subscription Website lead — ${name}${lead.testMode ? ' (TEST)' : ''}`, { autoClose: 8000 });
      break;
    case 'subscription_paid':
      toast.success(`🎉 Subscription Website active — ${name}${lead.testMode ? ' (TEST)' : ''}`, { autoClose: 8000 });
      break;
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const WebsiteLeadsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [submissions,     setSubmissions]     = useState<LeadEntry[]>([]);
  const [orders,          setOrders]          = useState<LeadEntry[]>([]);
  const [invoicePayments, setInvoicePayments] = useState<LeadEntry[]>([]);

  const [loading,       setLoading]       = useState(true);
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [isPanelOpen,   setIsPanelOpen]   = useState(false);

  // Track whether each collection's initial snapshot has been processed
  // so we don't fire toasts for pre-existing documents on mount.
  const initialLoadDone = useRef<Record<string, boolean>>({
    submissions:      false,
    orders:           false,
    invoice_payments: false,
  });

  // Track how many listeners have finished their first load
  const loadedCount = useRef(0);

  useEffect(() => {
    let unsubSubmissions:     (() => void) | null = null;
    let unsubOrders:          (() => void) | null = null;
    let unsubInvoicePayments: (() => void) | null = null;
    let cancelled = false;

    if (!isWebsiteConfigured) {
      setLoading(false);
      return;
    }

    signInWebsiteReader()
      .then(() => {
        if (cancelled) return;
        attachListeners();
      })
      .catch((err) => {
        console.error('[WebsiteLeads] sign-in failed:', err);
        setLoading(false);
      });

    function attachListeners() {
    // ── submissions ──────────────────────────────────────────────────────
    const qSub = query(
      collection(websiteDb!,'submissions'),
      orderBy('createdAt', 'desc')
    );

    unsubSubmissions = onSnapshot(qSub, (snap) => {
      setSubmissions(snap.docs.map((d) => submissionToLead(d.id, d.data() as Submission)));

      if (!initialLoadDone.current['submissions']) {
        initialLoadDone.current['submissions'] = true;
        loadedCount.current += 1;
        if (loadedCount.current >= 3) setLoading(false);
        return;
      }
      snap.docChanges().forEach((change) => {
        if (change.type === 'added') {
          toastForLead(submissionToLead(change.doc.id, change.doc.data() as Submission));
          setNewLeadsCount((c) => c + 1);
        }
      });
    }, (err) => console.error('[WebsiteLeads] submissions:', err));

    // ── orders ───────────────────────────────────────────────────────────
    const qOrd = query(
      collection(websiteDb!,'orders'),
      orderBy('createdAt', 'desc')
    );

    unsubOrders = onSnapshot(qOrd, (snap) => {
      setOrders(snap.docs.map((d) => orderToLead(d.id, d.data() as Order)));

      if (!initialLoadDone.current['orders']) {
        initialLoadDone.current['orders'] = true;
        loadedCount.current += 1;
        if (loadedCount.current >= 3) setLoading(false);
        return;
      }
      snap.docChanges().forEach((change) => {
        if (change.type === 'added') {
          toastForLead(orderToLead(change.doc.id, change.doc.data() as Order));
          setNewLeadsCount((c) => c + 1);
        }
        if (change.type === 'modified' && (change.doc.data() as Order).status === 'paid') {
          const d = change.doc.data() as Order;
          const name = [d.customerName || d.firstName, d.lastName].filter(Boolean).join(' ') || d.email;
          if (d.type === 'website_subscription') {
            toast.success(`🎉 Subscription Website — payment confirmed (${name})${d.testMode ? ' (TEST)' : ''}`, { autoClose: 8000 });
          } else {
            toast.success(`💰 Shopify audit payment confirmed — ${name}`, { autoClose: 6000 });
          }
        }
      });
    }, (err) => console.error('[WebsiteLeads] orders:', err));

    // ── invoice_payments ─────────────────────────────────────────────────
    const qPay = query(
      collection(websiteDb!,'invoice_payments'),
      orderBy('createdAt', 'desc')
    );

    unsubInvoicePayments = onSnapshot(qPay, (snap) => {
      setInvoicePayments(snap.docs.map((d) => paymentToLead(d.id, d.data() as InvoicePayment)));

      if (!initialLoadDone.current['invoice_payments']) {
        initialLoadDone.current['invoice_payments'] = true;
        loadedCount.current += 1;
        if (loadedCount.current >= 3) setLoading(false);
        return;
      }
      snap.docChanges().forEach((change) => {
        if (change.type === 'added') {
          toastForLead(paymentToLead(change.doc.id, change.doc.data() as InvoicePayment));
          setNewLeadsCount((c) => c + 1);
        }
        if (change.type === 'modified' && (change.doc.data() as InvoicePayment).status === 'paid') {
          const d = change.doc.data() as InvoicePayment;
          toast.success(`✅ Invoice paid — ${d.invoiceNumber} ($${d.amount?.toLocaleString()})`, { autoClose: 6000 });
        }
      });
    }, (err) => console.error('[WebsiteLeads] invoice_payments:', err));
    } // end attachListeners

    return () => {
      cancelled = true;
      unsubSubmissions?.();
      unsubOrders?.();
      unsubInvoicePayments?.();
    };
  }, []);

  // Unified feed — merge & sort newest first
  const leads: LeadEntry[] = [...submissions, ...orders, ...invoicePayments].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
  );

  const openLeadsPanel = () => {
    setIsPanelOpen(true);
    setNewLeadsCount(0);
  };

  const closeLeadsPanel = () => setIsPanelOpen(false);

  const deleteLead = async (lead: LeadEntry): Promise<void> => {
    if (!websiteDb) return;
    // Client-side delete as the minted reader identity — the website project's
    // rules grant UID web-construct-app-reader delete on the lead collections.
    try {
      await deleteDoc(doc(websiteDb, lead.collection, lead.id));
      toast.success('Entry deleted.', { autoClose: 3000 });
    } catch (err) {
      console.error('[WebsiteLeads] delete failed:', err);
      toast.error('Could not delete this entry.');
    }
  };

  return (
    <WebsiteLeadsContext.Provider
      value={{ leads, loading, newLeadsCount, isPanelOpen, openLeadsPanel, closeLeadsPanel, deleteLead }}
    >
      {children}
    </WebsiteLeadsContext.Provider>
  );
};

export const useWebsiteLeads = () => useContext(WebsiteLeadsContext);
